import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import EnquiryForm from '../components/EnquiryForm';
import { usePage } from '../contexts/CmsContext';
import {
  MapPin,
  Phone,
  Smartphone,
  Mail,
  Clock,
  Facebook,
  Instagram,
  Youtube,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

// Social media links (kept static — brand handles rarely change)
const socialLinks = {
  facebook: 'https://www.facebook.com/share/17Gp1FVd9i/',
  instagram: 'https://www.instagram.com/ssmch_jabalpur',
  youtube: 'https://youtube.com/@sukhsagarmedicalcollegeand6013',
};

const CONTACT_HERO_FALLBACK =
  'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&q=80';

const Contact = () => {
  const { data: cms } = usePage('contact');
  const heroImage = cms?.hero_image || CONTACT_HERO_FALLBACK;
  const emailNote = cms?.email_note || '';
  const office = cms?.office_hours || {
    days: 'Monday - Saturday',
    time: '9:00 AM - 5:00 PM',
    note: 'Closed on Sundays and Public Holidays',
  };
  const departments = Array.isArray(cms?.departments) ? cms.departments : [];

  // Derive the 5-row top phone tile automatically from the departments list —
  // no duplicate CMS field. For each department we surface a Landline row and
  // a Mobile row when the corresponding value is set.
  const topPhones = departments.flatMap((d) => {
    const rows = [];
    if (d.mobile) rows.push({ label: d.name, type: 'Mobile', number: d.mobile });
    if (d.landline) rows.push({ label: d.name, type: 'Landline', number: d.landline });
    return rows;
  });

  return (
    <div className="page-transition" data-testid="contact-page">
      {/* Hero */}
      <section
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="contact-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              Get in Touch
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Contact Us
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Have questions? We're here to help. Reach out to us through any of the channels below.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white" data-testid="contact-info-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 -mt-24 relative z-10">
            {/* Address tile */}
            <Card className="border-0 shadow-xl bg-white" data-testid="contact-info-address">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-[#4ECDC4]" size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#1A202C] mb-3">Address</h3>
                <p className="text-gray-600 text-sm">Sukh Sagar Medical College & Hospital</p>
                <p className="text-gray-600 text-sm">Block- Bargi, Mukanwara, Mouza, Chargawan</p>
                <p className="text-gray-600 text-sm">Jabalpur, Madhya Pradesh 482003</p>
              </CardContent>
            </Card>

            {/* Phone tile — five numbers with icon per type */}
            <Card className="border-0 shadow-xl bg-white" data-testid="contact-info-phone">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-[#4ECDC4]" size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#1A202C] mb-3 text-center">
                  Phone
                </h3>
                <div className="space-y-2">
                  {topPhones.length === 0 && (
                    <p className="text-gray-400 text-xs text-center">No numbers listed</p>
                  )}
                  {topPhones.map((p, i) => {
                    const isMobile = (p.type || '').toLowerCase() === 'mobile';
                    const Icon = isMobile ? Smartphone : Phone;
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-2 text-left"
                        data-testid={`contact-phone-row-${i}`}
                      >
                        <Icon size={14} className="text-[#4ECDC4] flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-[11px] text-gray-500 leading-tight">
                            {p.label} <span className="text-gray-400">({p.type})</span>
                          </p>
                          <a
                            href={`tel:${(p.number || '').replace(/\s+/g, '')}`}
                            className="text-sm text-[#1A202C] font-medium hover:text-[#4ECDC4] break-all"
                            data-testid={`contact-phone-num-${i}`}
                          >
                            {p.number}
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Email tile */}
            <Card className="border-0 shadow-xl bg-white" data-testid="contact-info-email">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-[#4ECDC4]" size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#1A202C] mb-3">Email</h3>
                <p className="text-gray-600 text-sm">admissions@ssmch.in</p>
                <p className="text-gray-600 text-sm">info@ssmch.in</p>
                {emailNote && <p className="text-xs text-gray-400 mt-2">{emailNote}</p>}
              </CardContent>
            </Card>

            {/* Office Hours tile */}
            <Card className="border-0 shadow-xl bg-white" data-testid="contact-info-hours">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-[#4ECDC4]" size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#1A202C] mb-3">
                  Office Hours
                </h3>
                <p className="text-gray-600 text-sm">{office.days}</p>
                <p className="text-gray-600 text-sm">{office.time}</p>
                {office.note && <p className="text-xs text-gray-400 mt-2">{office.note}</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="contact-main-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Send a Message
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Enquiry Form
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Fill out the form below and our team will get back to you within 24-48 hours.
              </p>
              <Card className="border-0 shadow-xl">
                <CardContent className="p-6">
                  <EnquiryForm sourcePage="contact" />
                </CardContent>
              </Card>
            </div>

            <div>
              <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Location
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Find Us
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Located at Block- Bargi, Mukanwara, Mouza, Chargawan, Jabalpur, Madhya Pradesh 482003. Our campus is well-connected and easily accessible.
              </p>

              <Card className="border-0 shadow-lg mb-8 overflow-hidden">
                <div className="h-64 bg-gray-200 relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3668.5!2d79.8430005!3d23.0577585!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3981ae11d35ee02b%3A0x74883fa8bb6b69f7!2sSUKH%20SAGAR%20MEDICAL%20COLLEGE%20%26%20HOSPITAL!5e0!3m2!1sen!2sin!4v1702900000000!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="SSMCH Location"
                  ></iframe>
                </div>
                <CardContent className="p-4 bg-gray-50">
                  <a
                    href="https://www.google.com/maps/place/SUKH+SAGAR+MEDICAL+COLLEGE+%26+HOSPITAL,/@23.0577585,79.8430005,17z"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#4ECDC4] hover:underline flex items-center justify-center gap-2"
                    data-testid="map-directions-link"
                  >
                    <MapPin size={14} />
                    Open in Google Maps for Directions
                  </a>
                </CardContent>
              </Card>

              {/* Department Contacts — landline + mobile per row */}
              <Card className="border-0 shadow-lg" data-testid="dept-contacts-card">
                <CardHeader className="bg-[#1A202C] text-white rounded-t-xl">
                  <CardTitle className="font-heading text-lg">Department Contacts</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100">
                    {departments.map((dept, index) => (
                      <div key={index} className="p-4" data-testid={`dept-contact-${index}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-[#1A202C]">{dept.name}</p>
                            <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                              {dept.landline && (
                                <p
                                  className="flex items-center gap-1.5"
                                  data-testid={`dept-${index}-landline`}
                                >
                                  <Phone size={12} className="text-[#4ECDC4]" />
                                  <span className="text-[11px] text-gray-400">Landline:</span>
                                  <a
                                    href={`tel:${dept.landline.replace(/\s+/g, '')}`}
                                    className="hover:text-[#4ECDC4]"
                                  >
                                    {dept.landline}
                                  </a>
                                </p>
                              )}
                              {dept.mobile && (
                                <p
                                  className="flex items-center gap-1.5"
                                  data-testid={`dept-${index}-mobile`}
                                >
                                  <Smartphone size={12} className="text-[#4ECDC4]" />
                                  <span className="text-[11px] text-gray-400">Mobile:</span>
                                  <a
                                    href={`tel:${dept.mobile.replace(/\s+/g, '')}`}
                                    className="hover:text-[#4ECDC4]"
                                  >
                                    {dept.mobile}
                                  </a>
                                </p>
                              )}
                            </div>
                          </div>
                          {dept.email && (
                            <a
                              href={`mailto:${dept.email}`}
                              className="text-[#4ECDC4] hover:text-[#3DBDB4] text-sm font-medium transition-colors whitespace-nowrap"
                              data-testid={`dept-${index}-email`}
                            >
                              {dept.email}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                    {departments.length === 0 && (
                      <p className="p-6 text-center text-sm text-gray-400">
                        No department contacts listed
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media */}
      <section className="py-16 bg-[#1A202C]" data-testid="social-section">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Connect With Us
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Follow us on social media to stay updated with the latest news and events.
          </p>
          <TooltipProvider delayDuration={200}>
            <div className="flex justify-center gap-5 flex-wrap">
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon social-icon-contact text-white"
                    aria-label="Facebook"
                    data-testid="contact-facebook"
                  >
                    <Facebook size={22} />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Follow us on Facebook</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon social-icon-contact text-white"
                    aria-label="Instagram"
                    data-testid="contact-instagram"
                  >
                    <Instagram size={22} />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Follow us on Instagram</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={socialLinks.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon social-icon-contact text-white"
                    aria-label="YouTube"
                    data-testid="contact-youtube"
                  >
                    <Youtube size={22} />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Subscribe on YouTube</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      </section>
    </div>
  );
};

export default Contact;
